/**
 * Utility to dynamically update meta tags for social media sharing (Open Graph)
 * This ensures that when links are shared on Telegram, Twitter, Facebook, etc.,
 * they display correct titles, descriptions, and images
 */

interface MetaTagConfig {
  title: string
  description: string
  image?: string
  url?: string
  type?: 'website' | 'article'
}

export const updateMetaTags = (config: MetaTagConfig) => {
  const { title, description, image, url, type = 'website' } = config

  // Update document title
  document.title = title

  // Update or create meta description
  updateMetaTag('description', description)

  // Open Graph tags for social media sharing
  updateMetaTag('og:title', title, 'property')
  updateMetaTag('og:description', description, 'property')
  updateMetaTag('og:type', type, 'property')

  if (url) {
    updateMetaTag('og:url', url, 'property')
  }

  if (image) {
    updateMetaTag('og:image', image, 'property')
    updateMetaTag('og:image:width', '1200', 'property')
    updateMetaTag('og:image:height', '630', 'property')
    updateMetaTag('og:image:type', 'image/jpeg', 'property')
  }

  // Twitter Card tags
  updateMetaTag('twitter:card', 'summary_large_image')
  updateMetaTag('twitter:title', title)
  updateMetaTag('twitter:description', description)
  if (image) {
    updateMetaTag('twitter:image', image)
  }
}

/**
 * Helper function to update or create a meta tag
 */
const updateMetaTag = (
  name: string,
  content: string,
  attribute: 'name' | 'property' = 'name'
) => {
  let element = document.querySelector(`meta[${attribute}="${name}"]`)

  if (!element) {
    element = document.createElement('meta')
    element.setAttribute(attribute, name)
    document.head.appendChild(element)
  }

  element.setAttribute('content', content)
}

/**
 * Reset meta tags to default values
 */
export const resetMetaTags = () => {
  updateMetaTags({
    title: 'Colozoo – Creating happiness!',
    description: 'Creative art supplies designed by artists for artists and families. Shop Colozoo on Amazon.',
  })
}
